import React, { useState } from 'react'
import {
  ChatMessage as ChatMessageType,
  ChatMessageSource,
  ChatMessageSourceMetadata,
} from '@/store/slices/ragChatSlice'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import { useNavigate } from 'react-router-dom'
import { 
  UserIcon,
  SparklesIcon,
  ClipboardDocumentIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  LinkIcon,
  ClockIcon,
  DocumentTextIcon,
  ChatBubbleLeftIcon,
  PaperClipIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline'
import { CheckIcon } from '@heroicons/react/24/solid'

interface ChatMessageProps {
  message: ChatMessageType
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message }) => {
  const [showSources, setShowSources] = useState(false)
  const [copied, setCopied] = useState(false)
  const navigate = useNavigate()
  
  const isUser = message.role === 'user'
  const hasMetadata = message.metadata && (
    message.metadata.sources?.length || 
    message.metadata.processing_time ||
    message.metadata.query_mode
  )

  // 自定義連結處理器
  const handleLinkClick = (href: string, event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    
    // 處理內部路由
    if (href.startsWith('/')) {
      if (href.startsWith('/issues/')) {
        // 議題連結 - 直接使用原始路徑
        navigate(href)
      } else if (href.startsWith('/uploads/')) {
        // 檔案連結，在新窗口打開
        window.open(href, '_blank')
      } else {
        // 其他內部連結
        navigate(href)
      }
    } else {
      // 外部連結，在新窗口打開
      window.open(href, '_blank', 'noopener,noreferrer')
    }
  }

  // 處理來源點擊導航
  const handleSourceClick = (source: ChatMessageSource) => {
    // 優先處理檔案路徑（[DC] 格式）
    if (source.content && source.content.includes('[DC]')) {
      const filePathMatch = source.content.match(/\[DC\]\s*(\/uploads\/files\/[^\s\]]+)/)
      if (filePathMatch) {
        const filePath = filePathMatch[1]
        window.open(filePath, '_blank')
        return
      }
    }

    const metadata = source.metadata
    if (!metadata) return

    let targetUrl = ''

    // 根據實體類型決定導航URL
    switch (metadata.entity_type) {
      case 'issue':
        if (metadata.issue_url) {
          targetUrl = metadata.issue_url
        } else if (metadata.entity_id) {
          targetUrl = `/issues/${metadata.entity_id}`
        }
        break
        
      case 'comment':
        if (metadata.comment_url) {
          targetUrl = metadata.comment_url
        } else if (metadata.issue_id) {
          targetUrl = `/issues/${metadata.issue_id}#comment-${metadata.entity_id}`
        }
        break
        
      case 'attachment':
        if (metadata.download_url) {
          // 對於附件，直接下載或在新頁面開啟
          window.open(metadata.download_url, '_blank')
          return
        }
        break
        
      case 'worklog':
        if (metadata.issue_url) {
          targetUrl = metadata.issue_url
        }
        break
    }

    if (targetUrl) {
      // 使用React Router的導航或直接跳轉
      window.location.href = targetUrl
    }
  }

  // 獲取來源實體的圖標
  const getSourceIcon = (
    entityType?: ChatMessageSourceMetadata['entity_type'],
    source?: ChatMessageSource,
  ) => {
    // 檢查是否是檔案來源
    if (source?.content && source.content.includes('[DC]')) {
      return PaperClipIcon
    }

    switch (entityType) {
      case 'issue':
        return DocumentTextIcon
      case 'comment':
        return ChatBubbleLeftIcon
      case 'attachment':
        return PaperClipIcon
      case 'worklog':
        return ClockIcon
      default:
        return LinkIcon
    }
  }

  // 獲取來源實體的顯示名稱
  const getSourceDisplayName = (source: ChatMessageSource) => {
    const metadata = source.metadata
    if (!metadata) {
      // 檢查內容中是否包含檔案路徑
      const content = source.content || ''
      const filePathMatch = content.match(/\[DC\]\s*(\/uploads\/files\/[^\s\]]+)/)
      if (filePathMatch) {
        const filePath = filePathMatch[1]
        const fileName = filePath.split('/').pop() || filePath
        return `📄 ${fileName}`
      }
      return '來源'
    }

    switch (metadata.entity_type) {
      case 'issue':
        return metadata.entity_key || metadata.entity_title || '議題'
      case 'comment':
        return `留言於 ${metadata.parent_entity_key || metadata.issue_key || '議題'}`
      case 'attachment':
        return metadata.original_name || metadata.filename || '附件'
      case 'worklog':
        return '工作日誌'
      default:
        return metadata.entity_type || '來源'
    }
  }

  // 獲取來源的副標題信息
  const getSourceSubtitle = (source: ChatMessageSource) => {
    const metadata = source.metadata
    if (!metadata) return ''

    switch (metadata.entity_type) {
      case 'issue':
        return metadata.project_name ? `專案: ${metadata.project_name}` : ''
      case 'comment':
        return metadata.issue_title || ''
      case 'attachment': {
        const parts: string[] = []
        if (metadata.issue_key) parts.push(`議題: ${metadata.issue_key}`)
        if (metadata.project_name) parts.push(`專案: ${metadata.project_name}`)
        return parts.join(' • ')
      }
      case 'worklog':
        return metadata.project_name ? `專案: ${metadata.project_name}` : ''
      default:
        return ''
    }
  }

  // 複製文字到剪貼板
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(message.content)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      console.error('複製失敗:', error)
    }
  }

  // 格式化處理時間
  const formatProcessingTime = (time?: number) => {
    if (!time) return ''
    if (time < 1000) return `${time}ms`
    return `${(time / 1000).toFixed(1)}s`
  }

  // 渲染來源信息
  const renderSources = () => {
    if (!message.metadata?.sources?.length) return null

    return (
      <div className="mt-3 border-t border-gray-100 pt-3">
        <button
          onClick={() => setShowSources(!showSources)}
          className="flex items-center text-xs text-gray-500 hover:text-gray-700 mb-2"
        >
          {showSources ? (
            <ChevronDownIcon className="h-3 w-3 mr-1" />
          ) : (
            <ChevronRightIcon className="h-3 w-3 mr-1" />
          )}
          <span>參考來源 ({message.metadata.sources.length})</span>
        </button>
        
        {showSources && (
          <div className="space-y-2">
            {message.metadata.sources.map((source, index) => {
              const IconComponent = getSourceIcon(source.metadata?.entity_type, source)
              const displayName = getSourceDisplayName(source)
              const subtitle = getSourceSubtitle(source)
              const metadata = source.metadata
              const hasEntityLink = Boolean(
                metadata?.issue_url ||
                  metadata?.comment_url ||
                  metadata?.download_url ||
                  metadata?.entity_id,
              )
              const hasDocumentLink = source.content.includes('[DC]')
              const isClickable = hasEntityLink || hasDocumentLink

              return (
                <div 
                  key={index} 
                  className={`
                    bg-gray-50 rounded p-3 text-xs transition-all duration-200
                    ${isClickable 
                      ? 'hover:bg-blue-50 hover:border-blue-200 border border-transparent cursor-pointer' 
                      : 'border border-gray-100'
                    }
                  `}
                  onClick={() => isClickable && handleSourceClick(source)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`
                        p-1 rounded 
                        ${source.metadata?.entity_type === 'issue' ? 'bg-blue-100 text-blue-600' :
                          source.metadata?.entity_type === 'comment' ? 'bg-green-100 text-green-600' :
                          source.metadata?.entity_type === 'attachment' ? 'bg-purple-100 text-purple-600' :
                          source.metadata?.entity_type === 'worklog' ? 'bg-orange-100 text-orange-600' :
                          'bg-gray-100 text-gray-600'
                        }
                      `}>
                        <IconComponent className="h-3 w-3" />
                      </div>
                      <div>
                        <div className="font-medium text-gray-800 flex items-center">
                          {displayName}
                          {isClickable && (
                            <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-1 text-gray-400" />
                          )}
                        </div>
                        {subtitle && (
                          <div className="text-gray-500 text-xs mt-0.5">
                            {subtitle}
                          </div>
                        )}
                      </div>
                    </div>
                    {source.score && (
                      <span className="text-blue-600 font-mono shrink-0 ml-2">
                        {(source.score * 100).toFixed(1)}%
                      </span>
                    )}
                  </div>
                  
                  <p className="text-gray-700 leading-relaxed">
                    {source.content.length > 150 
                      ? `${source.content.substring(0, 150)}...`
                      : source.content
                    }
                  </p>
                  
                    {metadata?.entity_type && (
                      <div className="mt-2 flex items-center justify-between">
                        <span className={`
                          px-2 py-1 rounded text-xs font-medium
                        ${metadata.entity_type === 'issue' ? 'bg-blue-100 text-blue-700' :
                          metadata.entity_type === 'comment' ? 'bg-green-100 text-green-700' :
                          metadata.entity_type === 'attachment' ? 'bg-purple-100 text-purple-700' :
                          metadata.entity_type === 'worklog' ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }
                      `}>
                        {metadata.entity_type}
                      </span>
                      {isClickable && (
                        <span className="text-xs text-blue-600 hover:text-blue-800">
                          點擊查看詳情
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // 渲染元數據
  const renderMetadata = () => {
    if (!hasMetadata) return null

    return (
      <div className="mt-2 flex items-center space-x-3 text-xs text-gray-500">
        {message.metadata?.processing_time && (
          <div className="flex items-center">
            <ClockIcon className="h-3 w-3 mr-1" />
            <span>{formatProcessingTime(message.metadata.processing_time)}</span>
          </div>
        )}
        
        {message.metadata?.query_mode && (
          <div className="px-2 py-1 bg-blue-50 text-blue-700 rounded">
            {message.metadata.query_mode}
          </div>
        )}
        
        <div className="flex items-center">
          <ClockIcon className="h-3 w-3 mr-1" />
          <span>{new Date(message.timestamp).toLocaleTimeString()}</span>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div 
        className={`
          max-w-xs lg:max-w-md px-4 py-3 rounded-lg relative group
          ${isUser 
            ? 'bg-blue-600 text-white rounded-br-sm' 
            : 'bg-gray-100 text-gray-800 rounded-bl-sm'
          }
        `}
      >
        {/* 頭像和角色指示器 */}
        <div className={`flex items-start space-x-2 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
          <div className={`
            flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center
            ${isUser ? 'bg-blue-500' : 'bg-white border-2 border-gray-200'}
          `}>
            {isUser ? (
              <UserIcon className="h-3 w-3 text-white" />
            ) : (
              <SparklesIcon className="h-3 w-3 text-blue-600" />
            )}
          </div>
          
          <div className="flex-1 min-w-0">
            {/* 訊息內容 */}
            <div className="prose prose-sm max-w-none break-words leading-relaxed">
              {isUser ? (
                // 使用者訊息保持純文字
                <div className="whitespace-pre-wrap">{message.content}</div>
              ) : (
                // AI 訊息支援 Markdown
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    // 自定義連結組件 - 增強樣式
                    a: ({ href, children, ...props }: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
                      <a
                        href={href ?? '#'}
                        onClick={href ? (e) => handleLinkClick(href, e) : undefined}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-2 py-1 rounded transition-all duration-200 font-medium underline cursor-pointer"
                        {...props}
                      >
                        {children}
                        {/* 根據連結類型顯示不同圖標 */}
                        {href?.startsWith('/issues/') && (
                          <DocumentTextIcon className="h-3 w-3 text-blue-500" />
                        )}
                        {href?.startsWith('/uploads/') && (
                          <PaperClipIcon className="h-3 w-3 text-purple-500" />
                        )}
                        {href?.startsWith('http') && (
                          <ArrowTopRightOnSquareIcon className="h-3 w-3 text-gray-400" />
                        )}
                      </a>
                    ),
                    // 自定義程式碼區塊樣式 - 改善對比度
                    code: ({ inline, children, ...props }: { inline?: boolean; children?: React.ReactNode }) => (
                      <code
                        className={
                          inline
                            ? 'bg-gray-200 text-gray-900 px-1.5 py-0.5 rounded text-sm font-mono border'
                            : 'block bg-gray-900 text-gray-100 p-3 rounded-lg text-sm font-mono overflow-x-auto border shadow-inner'
                        }
                        {...props}
                      >
                        {children}
                      </code>
                    ),
                    // 自定義標題樣式 - 增加層次感
                    h1: ({ children }) => (
                      <h1 className="text-lg font-bold mb-3 text-gray-900 border-b border-gray-200 pb-2">
                        {children}
                      </h1>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-base font-semibold mb-2 text-gray-800 mt-4">
                        {children}
                      </h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-sm font-semibold mb-2 text-gray-700 mt-3">
                        {children}
                      </h3>
                    ),
                    // 自定義列表樣式 - 改善間距和樣式
                    ul: ({ children }) => (
                      <ul className="list-disc list-outside pl-6 mb-3 space-y-1">
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal list-outside pl-6 mb-3 space-y-1">
                        {children}
                      </ol>
                    ),
                    li: ({ children }) => (
                      <li className="text-gray-700 leading-relaxed">
                        {children}
                      </li>
                    ),
                    // 自定義段落樣式 - 改善行距
                    p: ({ children }) => (
                      <p className="mb-3 last:mb-0 text-gray-800 leading-relaxed">
                        {children}
                      </p>
                    ),
                    // 自定義引用樣式
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-blue-300 pl-4 my-3 italic text-gray-600 bg-blue-50 py-2 rounded-r">
                        {children}
                      </blockquote>
                    ),
                    // 自定義表格樣式
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-3">
                        <table className="min-w-full border border-gray-200 rounded">
                          {children}
                        </table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="border border-gray-200 bg-gray-50 px-3 py-2 text-left font-semibold text-gray-700">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border border-gray-200 px-3 py-2 text-gray-800">
                        {children}
                      </td>
                    ),
                    // 自定義分隔線
                    hr: () => (
                      <hr className="my-4 border-gray-300" />
                    ),
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              )}
            </div>
            
            {/* 助手訊息的額外信息 */}
            {!isUser && (
              <>
                {renderMetadata()}
                {renderSources()}
              </>
            )}
          </div>
        </div>
        
        {/* 懸浮操作按鈕 */}
        <button
          onClick={copyToClipboard}
          className={`
            absolute top-1 right-1 opacity-0 group-hover:opacity-100 
            transition-opacity p-1 rounded hover:bg-black hover:bg-opacity-10
            ${isUser ? 'text-blue-100 hover:text-white' : 'text-gray-400 hover:text-gray-600'}
          `}
          title="複製訊息"
        >
          {copied ? (
            <CheckIcon className="h-3 w-3 text-green-500" />
          ) : (
            <ClipboardDocumentIcon className="h-3 w-3" />
          )}
        </button>
      </div>
    </div>
  )
}

export default ChatMessage
